import { useCallback, useMemo } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import * as yup from "yup";

const NFT_STANDARD_FIELDS = [
    {
        id: "collection",
        type: "text",
        name: "name",
        label: "Name to show (required)",
        input: {
            placeholder: "Enter the name of the collection"
        },
        tip: "The token’s name can’t be changed in the future"
    },
    {
        id: "symbol",
        type: "text",
        name: "symbol",
        label: "Symbol (required)",
        input: {
            placeholder: "Enter the token´s symbol"
        },
    },
    {
        id: "description",
        type: "text",
        name: "description",
        label: "Description (optional)",
        input: {
            placeholder: "Enter description on your token´s collection"
        },
    },
    {
        id: "short-url",
        type: "text",
        name: "shortURL",
        label: "Short URL",
        tip: "This is going to be your public URL"
    },
    {
        id: "royalties",
        type: "number",
        name: "royalties",
        label: "Royalties",
        input: {
            endAdornment: (
              <InputAdornment position="end">
                %
              </InputAdornment>
            )
        },
        tip: "Suggested: 0%, 10%, 20%, 30%"
    },
    {
        id: "max-supply",
        type: "number",
        name: "maxSupply",
        label: "Max Supply",
    },
    {
        id: "merkle-root",
        type: "string",
        name: "merkleRoot",
        label: "Merkle root for whitelist",
        tip: "Example: 0xd1e8aeb79500496ef3dc2e57ba746a8315d048b7a664a2bf948db4fa91960483"
    },
    {
        id: "sale-duration",
        type: "number",
        name: "saleDuration",
        label: "Sale Duration (in days)",
    },
]

const ERC721APSI_FIELDS = [
    {
        id: "collection",
        type: "text",
        name: "name",
        label: "Name to show (required)",
        input: {
            placeholder: "Enter the name of the collection"
        },
        tip: "The token’s name can’t be changed in the future"
    },
    {
        id: "symbol",
        type: "text",
        name: "symbol",
        label: "Symbol (required)",
        input: {
            placeholder: "Enter the token´s symbol"
        },
    },
    {
        id: "description",
        type: "text",
        name: "description",
        label: "Description (optional)",
        input: {
            placeholder: "Enter description on your token´s collection"
        },
    },
    {
        id: "royalties",
        type: "number",
        name: "royalties",
        label: "Royalties",
        input: {
            endAdornment: (
              <InputAdornment position="end">
                %
              </InputAdornment>
            )
        },
        tip: "Suggested: 0%, 10%, 20%, 30%"
    },
    {
        id: "max-supply",
        type: "number",
        name: "maxSupply",
        label: "Max Supply",
    },
    {
        id: "merkle-root",
        type: "string",
        name: "merkleRoot",
        label: "Merkle root for whitelist",
        tip: "Example: 0xd1e8aeb79500496ef3dc2e57ba746a8315d048b7a664a2bf948db4fa91960483"
    },
    {
        id: "sale-duration",
        type: "number",
        name: "saleDuration",
        label: "Sale Duration (in days)",
    },
]

const ERC1155_FIELDS = [
    {
        id: "collection",
        type: "text",
        name: "name",
        label: "Name to show (required)",
        input: {
            placeholder: "Enter the name of the collection"
        },
        tip: "The token’s name can’t be changed in the future"
    },
    {
        id: "symbol",
        type: "text",
        name: "symbol",
        label: "Symbol (required)",
        input: {
            placeholder: "Enter the token´s symbol"
        },
    },
    {
        id: "description",
        type: "text",
        name: "description",
        label: "Description (optional)",
        input: {
            placeholder: "Enter description on your token´s collection"
        },
    },
    {
        id: "short-url",
        type: "text",
        name: "shortURL",
        label: "Short URL",
        tip: "This is going to be your public URL"
    },
    {
        id: "royalties",
        type: "number",
        name: "royalties",
        label: "Royalties",
        input: {
            endAdornment: (
              <InputAdornment position="end">
                %
              </InputAdornment>
            )
        },
        tip: "Suggested: 0%, 10%, 20%, 30%"
    },
    {
        id: "max-supply",
        type: "number",
        name: "maxSupply",
        label: "Max Supply",
    },
]

const ERC20_FIELDS = [
    {
        id: "collection",
        type: "text",
        name: "name",
        label: "Name to show (required)",
        input: {
            placeholder: "Enter the name of the collection"
        },
        tip: "The token’s name can’t be changed in the future"
    },
    {
        id: "symbol",
        type: "text",
        name: "symbol",
        label: "Symbol (required)",
        input: {
            placeholder: "Enter the token´s symbol"
        },
    },
    {
        id: "max-supply",
        type: "number",
        name: "maxSupply",
        label: "Max Supply",
    },
]

const useContractFormDataByType = (contractType) => {

    const schema = useMemo(() => {
        switch(contractType) {
            case "ERC721":
                return yup.object().shape({
                    name: yup.string().min(3, "Very short name for collection").required("Collection name is required"),
                    symbol: yup.string().required("Symbol is required"),
                    description: yup.string(),
                    shortURL: yup.string().url(),
                    royalties: yup.number().max(100, "Maximum is 100%").min(0, "Minimum is 0%"),
                    maxSupply: yup.number().min(0, "Max supply should be >= 0"),
                    merkleRoot: yup.string().matches(new RegExp("0x[0-9a-fA-F]{64}") || "", "Incorrect merkle root provided"),
                    saleDuration: yup.number().min(0, "Sale duration should be >= 0")
                });
            case "ERC721A":
            case "ERC721PSI":
                return yup.object().shape({
                    name: yup.string().min(3, "Very short name for collection").required("Collection name is required"),
                    symbol: yup.string().required("Symbol is required"),
                    description: yup.string(),
                    royalties: yup.number().max(100, "Maximum is 100%").min(0, "Minimum is 0%"),
                    maxSupply: yup.number().min(0, "Max supply should be >= 0"),
                    merkleRoot: yup.string().matches(new RegExp("0x[0-9a-fA-F]{64}") || "", "Incorrect merkle root provided"),
                    saleDuration: yup.number().min(0, "Sale duration should be >= 0")
                });
            case "ERC1155":
                return yup.object().shape({
                    name: yup.string().min(3, "Very short name for collection").required("Collection name is required"),
                    symbol: yup.string().required("Symbol is required"),
                    description: yup.string(),
                    shortURL: yup.string().url(),
                    royalties: yup.number().max(100, "Maximum is 100%").min(0, "Minimum is 0%"),
                    maxSupply: yup.number().min(0, "Max supply should be >= 0"),
                });
            case "ERC20":
                return yup.object().shape({
                    name: yup.string().min(3, "Very short name for token").required("Token name is required"),
                    symbol: yup.string().required("Symbol is required"),
                    maxSupply: yup.number()
                    // description: yup.string(),
                });
            default:
                return yup.object().shape();
        }
    }, [contractType]);

    const formFieldsData = useMemo(() => {
        switch(contractType) {
            case "ERC721":
                return NFT_STANDARD_FIELDS;
            case "ERC721A":
            case "ERC721PSI":
                return ERC721APSI_FIELDS;
            case "ERC1155":
                return ERC1155_FIELDS;
            case "ERC20":
                return ERC20_FIELDS;
            default:
                return [];
        }
    }, [contractType]);

    const getInitialValues = useCallback(() => {
        switch(contractType) {
            case "ERC721":
                return {
                    name: "",
                    symbol: "",
                    description: "",
                    shortURL: "https://www.certifylabs.com/",
                    royalties: 0,
                    maxSupply: 0,
                    merkleRoot: "",
                    saleDuration: 0
                }
            case "ERC721A":
            case "ERC721PSI":
                return {
                    name: "",
                    symbol: "",
                    description: "",
                    royalties: 0,
                    maxSupply: 0,
                    merkleRoot: "",
                    saleDuration: 0
                }
            case "ERC1155":
                return {
                    name: "",
                    symbol: "",
                    description: "",
                    shortURL: "https://www.certifylabs.com/",
                    maxSupply: 0,
                }
            case "ERC20":
                return {
                    name: "",
                    symbol: "",
                    maxSupply: 0
                }
            default:
                return {};
        }
    }, [contractType]);

    return { formFieldsData, getInitialValues, schema }
}

export { useContractFormDataByType };